import { LinkedinSearchCategoryType } from 'crono-fe-common/constants/constants';
import { LinkedinFilterValue } from 'crono-fe-common/types/crono-extension/linkedin';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import SubscriptionPlanType from 'crono-fe-common/types/enums/subscriptionPlanType';
import {
  CallFeedback,
  CallSentiment,
  QuickAction,
} from 'crono-fe-common/types/logCall';
import { getLabelForIntegrationType } from 'crono-fe-common/utils/crm-utils';
import { ENDPOINTS } from 'config/endpoints';
import PATH from 'crono-fe-common/constants/path';

export class FeConstants {
  static emailEditorPlugins =
    'advlist autolink lists link image charmap print preview anchor ' +
    'searchreplace visualblocks code fullscreen ' +
    'insertdatetime media table paste code wordcount link';

  static emailEditorToolbar =
    'undo redo |  fontsize ' +
    'bold italic backcolor forecolor attachmentsButton  templatesButton variablesButton |  |lineheight |fontfamily | alignleft aligncenter ' +
    'alignright alignjustify | bullist numlist outdent indent | ' +
    ' link image';
  static emailEditorTemplateToolbar =
    'undo redo |  fontsize ' +
    'bold italic backcolor forecolor attachmentsButton templatesButton |  |lineheight |fontfamily | alignleft aligncenter ' +
    'alignright alignjustify | bullist numlist outdent indent | ' +
    ' link image';

  static linkedinEditorToolbar =
    'undo redo | attachmentsButton variablesButton';
  static linkedinInvitationEditorToolbar = 'undo redo | variablesButton';

  static editorToolbarFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'script',
    'blockquote',
    'indent',
    'width',
    'code-block',
    'placeholder',
  ];

  static editorToolbarAdvanceFormattingFormats = [
    'list',
    'bullet',
    'link',
    'html-blot',
    'color',
    'background',
    'size',
    'font',
    'align',
    'signature',
  ];

  static htmlBlotTagName = 'section';
  static signatureDataAttribute = 'data-crono-signature';
  static editorSupportedFonts = [
    'Andale Mono',
    'Arial-Black',
    'Book Antiqua',
    'Comic Sans MS',
    'Courier New',
    'Geneva',
    'Helvetica',
    'Impact',
    'Symbol',
    'Tahoma',
    'Terminal',
    'Times New Roman',
    'Verdana',
    'Roboto',
    'Monospace',
    'Trebuchet MS',
  ];

  static editorSupportedFontSizes = [
    '8pt',
    '10pt',
    '12pt',
    '14pt',
    '18pt',
    '24pt',
    '36pt',
  ];

  static SALESFORCE_LOGIN_URL = `https://login.salesforce.com/services/oauth2/authorize?response_type=code&client_id=${
    process.env.VITE_APP_SALESFORCE_CLIENT_ID
  }&redirect_uri=${encodeURI(
    process.env.VITE_APP_FE_URL as string,
  )}/connect/salesforce/callback`;

  static SALESFORCE_MANAGED_PACKAGE_URL =
    process.env.VITE_APP_ENVIRONMENT === 'prod'
      ? 'https://login.salesforce.com/packaging/installPackage.apexp?p0=04td2000000OnZ3AAK'
      : 'https://login.salesforce.com/packaging/installPackage.apexp?p0=04td2000000OmebAAC';

  static OKTA_SSO_LOGIN_URL = (email: string, returnUrl: string) =>
    `${process.env.VITE_APP_BE_URL}/${
      ENDPOINTS.auth.sso.loginOkta
    }?email=${encodeURIComponent(email)}&returnUrl=${encodeURIComponent(
      returnUrl,
    )}`;

  static MICROSOFT_SSO_LOGIN_URL = (returnUrl: string) =>
    `${process.env.VITE_APP_BE_URL}/${
      ENDPOINTS.auth.sso.loginMicrosoft
    }?returnUrl=${encodeURIComponent(returnUrl)}`;

  static GOOGLE_SSO_LOGIN_URL = (returnUrl: string) =>
    `${process.env.VITE_APP_BE_URL}/${
      ENDPOINTS.auth.sso.loginGoogle
    }?returnUrl=${encodeURIComponent(returnUrl)}`;

  static gmailScopes =
    'email https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly';
  static gmailModifyScope = 'https://www.googleapis.com/auth/gmail.modify';
  static calendarScopes =
    'openid profile email https://www.googleapis.com/auth/calendar.events';

  static hubspotScopes =
    'crm.objects.contacts.read crm.objects.contacts.write crm.objects.contacts.read crm.objects.contacts.write crm.objects.companies.write crm.objects.companies.read crm.objects.deals.write crm.objects.deals.read crm.objects.owners.read sales-email-read conversations.read';

  // Creating CRM properties for Crono-only fields needs the schema write
  // scopes. Every connect asks for all the optional scopes: a re-consent
  // replaces them, so one asking for fewer would drop those granted before.
  static hubspotPropertyCreationScopes =
    'crm.schemas.contacts.write crm.schemas.companies.write crm.schemas.deals.write';

  static hubspotOptionalScopes = `crm.lists.read ${FeConstants.hubspotPropertyCreationScopes}`;

  static HUBSPOT_LOGIN_URL = (clientId: string | null | undefined) =>
    `https://app.hubspot.com/oauth/authorize?client_id=${
      clientId || process.env.VITE_APP_HUBSPOT_CLIENT_ID
    }&redirect_uri=${encodeURI(
      process.env.VITE_APP_FE_URL as string,
    )}/connect/hubspot/callback&scope=${encodeURIComponent(
      FeConstants.hubspotScopes,
    )}&optional_scope=${encodeURIComponent(FeConstants.hubspotOptionalScopes)}`;

  static PIPEDRIVE_LOGIN_URL = `https://oauth.pipedrive.com/oauth/authorize?client_id=${
    process.env.VITE_APP_PIPEDRIVE_CLIENT_ID
  }&redirect_uri=${encodeURI(
    process.env.VITE_APP_FE_URL as string,
  )}/connect/pipedrive/callback`;

  // Attio grants scopes at app level in the Developer Console, so the authorize
  // URL carries no `scope` parameter. The console also registers a fixed
  // redirect URI, which Attio rejects any mismatch against — so a front-end
  // whose own origin is not the registered one (local development) points
  // VITE_APP_ATTIO_REDIRECT_URI at the registered callback instead. Local and
  // dev share a back end, so the connection the dev callback persists is the
  // one Attio/Verify reads back locally. Every deployed env file has to set the
  // variable too: Vite loads .env.local in all modes, so a key defined only
  // there leaks its local value into the dev and prod bundles.
  static ATTIO_REDIRECT_URI =
    process.env.VITE_APP_ATTIO_REDIRECT_URI ||
    `${process.env.VITE_APP_FE_URL}/connect/attio/callback`;

  static ATTIO_LOGIN_URL = `https://app.attio.com/authorize?response_type=code&client_id=${
    process.env.VITE_APP_ATTIO_CLIENT_ID
  }&redirect_uri=${encodeURIComponent(FeConstants.ATTIO_REDIRECT_URI)}`;

  static zohoScopes =
    'ZohoCRM.modules.ALL,ZohoCRM.settings.ALL,ZohoCRM.users.READ,ZohoCRM.org.READ,ZohoCRM.notifications.ALL,ZohoCRM.coql.READ';

  // Same constraint as Attio: the back end exchanges the code against the
  // redirect URI registered on the Zoho client, so a local front end points
  // VITE_APP_ZOHO_REDIRECT_URI at the registered dev callback.
  static ZOHO_REDIRECT_URI =
    process.env.VITE_APP_ZOHO_REDIRECT_URI ||
    `${process.env.VITE_APP_FE_URL}/connect/zoho/callback`;

  // The US accounts server signs in users of every data center; the redirect
  // then names the one that issued the code. Zoho only issues a refresh token
  // with `access_type=offline` + `prompt=consent`, and the back end rejects a
  // connection without one.
  static ZOHO_LOGIN_URL = (state: string) =>
    `https://accounts.zoho.com/oauth/v2/auth?response_type=code&client_id=${
      process.env.VITE_APP_ZOHO_CLIENT_ID
    }&redirect_uri=${encodeURIComponent(
      FeConstants.ZOHO_REDIRECT_URI,
    )}&scope=${encodeURIComponent(
      FeConstants.zohoScopes,
    )}&access_type=offline&prompt=consent&state=${encodeURIComponent(state)}`;

  // Registered on the Entra app for the dev and prod hosts only, so a local
  // front end points VITE_APP_DYNAMICS_REDIRECT_URI at the dev callback.
  static DYNAMICS_REDIRECT_URI =
    process.env.VITE_APP_DYNAMICS_REDIRECT_URI ||
    `${process.env.VITE_APP_FE_URL}/connect/dynamics/callback`;

  // The double slash is part of the Global Discovery scope.
  static dynamicsDiscoveryScope =
    'https://globaldisco.crm.dynamics.com//user_impersonation';

  private static DYNAMICS_AUTHORIZE_URL = (
    authority: string,
    scope: string,
    state: string,
    prompt?: string,
  ) =>
    `https://login.microsoftonline.com/${authority}/oauth2/v2.0/authorize?client_id=${
      process.env.VITE_APP_DYNAMICS_CLIENT_ID
    }&response_type=code&response_mode=query&redirect_uri=${encodeURIComponent(
      FeConstants.DYNAMICS_REDIRECT_URI,
    )}&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}${
      prompt ? `&prompt=${prompt}` : ''
    }`;

  // Sign-in 1 lists the environments. `organizations` admits only work or
  // school accounts, and `select_account` stops a browser signed in to several
  // Microsoft accounts from silently picking the wrong one.
  static DYNAMICS_DISCOVERY_LOGIN_URL = (state: string) =>
    FeConstants.DYNAMICS_AUTHORIZE_URL(
      'organizations',
      FeConstants.dynamicsDiscoveryScope,
      state,
      'select_account',
    );

  // Sign-in 2 connects the picked environment, at the tenant that owns it so a
  // guest user signs in there. Without `offline_access` Microsoft issues no
  // refresh token and the back end rejects the connection.
  static DYNAMICS_ENVIRONMENT_LOGIN_URL = (
    state: string,
    tenantId: string | null,
    environmentUrl: string,
  ) =>
    FeConstants.DYNAMICS_AUTHORIZE_URL(
      tenantId || 'organizations',
      `${environmentUrl.replace(/\/+$/, '')}/user_impersonation offline_access openid`,
      state,
    );

  static AIRCALL_LOGIN_URL = `https://dashboard.aircall.io/oauth/authorize?client_id=${
    process.env.VITE_APP_AIRCALL_CLIENT_ID
  }&redirect_uri=${encodeURI(
    process.env.VITE_APP_FE_URL as string,
  )}/connect/aircall/callback&response_type=code&scope=public_api`;

  static outlookScopes =
    'openid offline_access https://graph.microsoft.com/mail.send https://graph.microsoft.com/mail.read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/User.Read https://graph.microsoft.com/Calendars.ReadWrite';

  static outlookScopesMailOnly =
    'openid offline_access https://graph.microsoft.com/mail.send https://graph.microsoft.com/mail.read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/User.Read';

  static OUTLOOK_LOGIN_URL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${
    process.env.VITE_APP_OUTLOOK_CLIENT_ID
  }&redirect_uri=${encodeURI(
    process.env.VITE_APP_FE_URL as string,
  )}/connect/outlook/callback&response_type=code&response_mode=query&scope=${encodeURIComponent(
    FeConstants.outlookScopes,
  )}&prompt=select_account`;

  static OUTLOOK_LOGIN_URL_MAIL_ONLY = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${
    process.env.VITE_APP_OUTLOOK_CLIENT_ID
  }&redirect_uri=${encodeURI(
    process.env.VITE_APP_FE_URL as string,
  )}/connect/outlook/callback&response_type=code&response_mode=query&scope=${encodeURIComponent(
    FeConstants.outlookScopesMailOnly,
  )}&prompt=select_account`;

  // Bot scopes the Crono Slack app needs; the backend validates the granted set
  // on the token exchange, so this list has to stay in sync with it.
  static slackWorkspaceScopes =
    'app_mentions:read,channels:read,channels:join,groups:read,chat:write,im:write,im:history,reactions:write';

  // Slack requires the `redirect_uri` sent here to match the one the backend
  // rebuilds from its own `App:BaseUrl` on the token exchange, and that is
  // always the deployed host — so this deliberately ignores VITE_APP_FE_URL and
  // a local FE finishes the install on the dev host.
  static slackOauthReturnHost =
    process.env.VITE_APP_ENVIRONMENT === 'prod'
      ? 'https://app.crono.one'
      : 'https://dev.app.crono.one';

  static SLACK_WORKSPACE_INSTALL_URL = (state: string) =>
    `https://slack.com/oauth/v2/authorize?client_id=${
      process.env.VITE_APP_SLACK_CLIENT_ID
    }&scope=${encodeURIComponent(
      FeConstants.slackWorkspaceScopes,
    )}&redirect_uri=${encodeURIComponent(
      `${FeConstants.slackOauthReturnHost}${PATH.OAUTH_SLACK}`,
    )}&state=${encodeURIComponent(state)}`;

  static slackUserScopes = 'openid,email';

  // The personal link is OpenID Connect, not `oauth/v2/authorize` with
  // `user_scope` — the latter returns a code the backend cannot redeem, because
  // the two flows are exchanged against different Slack token endpoints.
  static SLACK_USER_LINK_URL = (state: string) =>
    `https://slack.com/openid/connect/authorize?response_type=code&client_id=${
      process.env.VITE_APP_SLACK_CLIENT_ID
    }&scope=${encodeURIComponent(
      FeConstants.slackUserScopes,
    )}&redirect_uri=${encodeURIComponent(
      `${FeConstants.slackOauthReturnHost}${PATH.OAUTH_SLACK_USER}`,
    )}&state=${encodeURIComponent(state)}`;

  static emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  static phoneRegex =
    /((\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}/;
  static linkedinRegex =
    /^(http(s)?:\/\/)?([\w]+\.)?linkedin\.com\/(pub|in|profile|company)\/([-a-zA-Z0-9]+)\/*/gm;

  static maxImportAccounts = 1000;
  static maxImportAccountsCronoIntegration = 1000;
  static maxImportProspects = 1000;
  static maxImportProspectsCronoIntegration = 1000;

  static externalPropertyProspectsInitialIndex = 9;
  static externalPropertyAccountsInitialIndex = 4;
  static externalPropertyDealsInitialIndex = 6;
  static externalPropertyDealsInitialIndexEditColumns = 5;

  static datePickerDefaultYearsOffset = 5;

  static uploadFileLimitInMb = 10;
  static uploadImageLimitInMb = 5;
  static maxLimitReachedMessage = `Selected files will exceed the maximum limit of ${FeConstants.uploadFileLimitInMb} MB.`;
  static maxFilesPerTransaction = 5;

  static VoiceNoteContent = 'LinkedIn Voice Note';

  static emailBoundary = 'foo_bar_baz';

  static defaultNewStrategyName = 'New sequence';
  static defaultNewStrategyId = -99;

  static cronoCsrfCookie = process.env.VITE_APP_CRONO_CSRF_COOKIE as string;

  static cronoCsrfLocalCookie = process.env
    .VITE_APP_CRONO_CSRF_LOCAL_COOKIE as string;

  static cronoThankYouCookie = process.env
    .VITE_APP_CRONO_THANK_YOU_COOKIE as string;

  static actionsProgressBatch = 20;

  static minStepsForStrategy = 1;
  static maxStepsForStrategy = 16;

  static minLicenseAmount = 1;
  static maxLicenseAmount = 100;

  static MAX_DELAY = 365;
  static MIN_DELAY = 0;

  static strategyLimit = 20;

  static contactPageLimit = 10;
  static dealsPageLimit = 10;
  static opportunitySearchLimit = 20;
  static usersPageLimit = 10;
  static allUsersWithTeamsLimit = 200;

  static freeTrialLength = 7;

  static opportunityStageLimit = 20;

  static MINIMUM_STATUSES = 3;

  static MAX_STATUSES = 20;

  static defaultImageUrl =
    'https://crono-assets.s3.eu-central-1.amazonaws.com/defaultCompanyLogo.png';

  static defaultOptOutMessage = 'Would you like to opt-out?';

  static quickActions = [
    { id: 0, title: 'Call Back', type: QuickAction.CALL_BACK },
    { id: 1, title: 'Stop Prospect', type: QuickAction.STOP_PROSPECT },
    { id: 2, title: 'Nurture', type: QuickAction.NURTURE },
    { id: 3, title: 'Not in target', type: QuickAction.INACTIVE },
    { id: 4, title: 'Meeting', type: QuickAction.CALL_SCHEDULED },
  ];

  static callFeedback = [
    { id: 1, title: 'Wrong Number', type: CallFeedback.WRONG_NUMBER },
    { id: 2, title: 'Not Answered', type: CallFeedback.NOT_ANSWERED },
    { id: 3, title: 'Busy', type: CallFeedback.BUSY },
    { id: 4, title: 'Voicemail', type: CallFeedback.VOICEMAIL },
    { id: 5, title: 'Gatekeeper', type: CallFeedback.GATE_KEEPER },
    { id: 6, title: 'Connected', type: CallFeedback.CONNECTED },
  ];

  static callSentiment = [
    { id: 1, title: 'Not interested', type: CallSentiment.NOT_INTERESTED },
    { id: 2, title: 'Call back', type: CallSentiment.CALL_BACK },
    {
      id: 3,
      title: 'Meeting scheduled',
      type: CallSentiment.MEETING_SCHEDULED,
    },
    {
      id: 4,
      title: 'Not right contact',
      type: CallSentiment.NOT_RIGHT_CONTACT,
    },
    { id: 5, title: 'Send email', type: CallSentiment.SEND_EMAIL },
    { id: 6, title: 'Personal number', type: CallSentiment.PERSONAL_NUMBER },
  ];

  static notificationTimeSpan = 4 * 60 * 60 * 1000; // 4 hours

  static gmailInstructionURL = 'https://admin.google.com/ac/owl';

  static gmailClientId =
    '228143010081-5tqb4q8gdnmik5nb3ap976hs70rp7njg.apps.googleusercontent.com';

  static gmailGuideText = `
  1. Whitelist Crono App on Google Workspace. To make sure Crono can safely interact with your Gmail accounts please follow these steps.
  2. Go to this admin page to set up a third-party app. Click on configure new app and select “Client ID”.
  3. Paste our Client ID (${this.gmailClientId}) and click “Search”.
  4. Click “Select” on Crono App.
  5. Set app access to “Trusted” and click “Configure”. Your organization can now connect to Crono!
  6. Now click “Connect” below here to connect your Gmail Account`;

  static abTestingOptions =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  static websitePricingURL = 'http://www.crono.one/pricing';

  static websiteURL = 'http://www.crono.one/';

  static placeholderUrl =
    'https://www.youtube.com/embed/cvh0nX08nRw?si=brVa44uA9XduRrTf';

  static cronoTermsAndCondition = 'https://www.crono.one/terms/';
  static cronoPrivacyPolicy = 'https://www.crono.one/privacy/';

  static MAX_API_KEYS_ALLOWED = 5;

  static LIST_STORAGE_KEYS = {
    ACCOUNT: 'listAccount',
    PROSPECT: 'listProspect',
    LEAD: 'listLead',
    TEMPLATE: 'listTemplate',
    STRATEGY: 'listStrategy',
  };

  static REDITUS_REFERRAL_BOUNTY = '250$';

  static DEFAULT_MAILBOX_AUTOMATION_DELAY_STEPS_SECS = [
    5, 10, 15, 20, 30, 60, 100, 150, 300,
  ];

  static MAILBOX_AUTOMATION_WINDOW_HOURS = {
    default: 1,
    min: 1,
    max: 8,
  };
}

// [INFO] Labels are in AI FC. If you need to update them, please update also there.
export const linkedinGeoUrns: LinkedinSearchCategoryType[] = [
  { value: '101452733', label: 'Australia' },
  { value: '107042567', label: 'Adelaide, Australia' },
  { value: '100425729', label: 'Bahrain' },
  { value: '104468365', label: 'Brisbane, Australia' },
  { value: '106089960', label: 'Canberra, Australia' },
  { value: '100992797', label: 'Melbourne, Australia' },
  { value: '103392068', label: 'Perth, Australia' },
  { value: '104769905', label: 'Sydney, Australia' },
  { value: '103883259', label: 'Austria' },
  { value: '100565514', label: 'Belgium' },
  { value: '101174742', label: 'Canada' },
  { value: '101728226', label: 'Montreal, Canada' },
  { value: '106234700', label: 'Ottawa, Canada' },
  { value: '100025096', label: 'Toronto, Canada' },
  { value: '103366113', label: 'Vancouver, Canada' },
  { value: '104514075', label: 'Denmark' },
  { value: '102974008', label: 'Estonia' },
  { value: '105015875', label: 'France' },
  { value: '106383538', label: 'Paris, France' },
  { value: '103815258', label: 'Lyon, France' },
  { value: '101282230', label: 'Germany' },
  { value: '100477049', label: 'Munich, Germany' },
  { value: '103035651', label: 'Berlin, Germany' },
  { value: '90009714', label: 'Frankfurt Rhine, Germany' },
  { value: '103239229', label: 'Kuwait' },
  { value: '102478259', label: 'Indonesia' },
  { value: '102644903', label: 'Bekasi, Indonesia' },
  { value: '102708774', label: 'Bogor, Indonesia' },
  { value: '106331850', label: 'Depok, Indonesia' },
  { value: '104370960', label: 'Jakarta, Indonesia' },
  { value: '105898461', label: 'Tangerang, Indonesia' },
  { value: '104738515', label: 'Ireland' },
  { value: '103350119', label: 'Italy' },
  { value: '102873640', label: 'Milan, Italy' },
  { value: '102334534', label: 'Rome, Italy' },
  { value: '106742401', label: 'Turin, Italy' },
  { value: '104948657', label: 'Brescia, Italy' },
  { value: '105768355', label: 'Bologna, Italy' },
  { value: '101544895', label: 'Florence, Italy' },
  { value: '102361685', label: 'Naples, Italy' },
  { value: '104843836', label: 'Abruzzo, Italy' },
  { value: '106271685', label: 'Basilicata, Italy' },
  { value: '104193506', label: 'Calabria, Italy' },
  { value: '101168731', label: 'Campania, Italy' },
  { value: '101165094', label: 'Emilia Romagna, Italy' },
  { value: '100700905', label: 'Friuli-Venezia Giulia, Italy' },
  { value: '104758761', label: 'Lazio, Italy' },
  { value: '106409273', label: 'Liguria, Italy' },
  { value: '102960788', label: 'Lombardia, Italy' },
  { value: '104590035', label: 'Marche, Italy' },
  { value: '101052278', label: 'Molise, Italy' },
  { value: '104396997', label: 'Piemonte, Italy' },
  { value: '103477765', label: 'Puglia, Italy' },
  { value: '106056685', label: 'Sicilia, Italy' },
  { value: '105265841', label: 'Sardegna, Italy' },
  { value: '101083819', label: 'Toscana, Italy' },
  { value: '105285498', label: 'Trentino-Alto Adige, Italy' },
  { value: '102840047', label: 'Umbria, Italy' },
  { value: '100633933', label: 'Valle D’Aosta, Italy' },
  { value: '106933435', label: 'Veneto, Italy' },
  { value: '104341318', label: 'Latvia' },
  { value: '101464403', label: 'Lithuania' },
  { value: '104042105', label: 'Luxemburg' },
  { value: '106808692', label: 'Malaysia' },
  { value: '107510587', label: 'Johor Bahru, Malaysia' },
  { value: '105563270', label: 'Kuala Lumpur, Malaysia' },
  { value: '105521927', label: 'Selangor, Malaysia' },
  { value: '103323778', label: 'Mexico' },
  { value: '102890719', label: 'Netherlands' },
  { value: '103619019', label: 'Oman' },
  { value: '101762205', label: 'Muscat, Oman' },
  { value: '105072130', label: 'Poland' },
  { value: '100364837', label: 'Portugal' },
  { value: '102775852', label: 'Lisbon, Portugal' },
  { value: '104170880', label: 'Qatar' },
  { value: '106203786', label: 'Doha, Qatar' },
  { value: '100459316', label: 'Saudi Arabia' },
  { value: '109019211', label: 'Jeddah, Saudi Arabia' },
  { value: '101336206', label: 'Riyadh, Saudi Arabia' },
  { value: '102454443', label: 'Singapore' },
  { value: '105646813', label: 'Spain' },
  { value: '101734461', label: 'Alicante, Spain' },
  { value: '105088894', label: 'Barcelona, Spain' },
  { value: '101962740', label: 'Bilbao, Spain' },
  { value: '100994331', label: 'Madrid, Spain' },
  { value: '104401670', label: 'Malaga, Spain' },
  { value: '105512687', label: 'Valencia, Spain' },
  { value: '106693272', label: 'Switzerland' },
  { value: '105117694', label: 'Sweden' },
  { value: '105146118', label: 'Thailand' },
  { value: '106343449', label: 'Bangkok, Thailand' },
  { value: '104114836', label: 'Göteborg, Sweden' },
  { value: '101759788', label: 'Malmö, Sweden' },
  { value: '104853962', label: 'Stockholm, Sweden' },
  { value: '101165590', label: 'United Kingdom' },
  { value: '102257491', label: 'London, United Kingdom' },
  { value: '114456426', label: 'Manchester, United Kingdom' },
  { value: '102264497', label: 'Ukraine' },
  { value: '104305776', label: 'United Arab Emirates' },
  { value: '106204383', label: 'Dubai, United Arab Emirates' },
  { value: '103720977', label: 'Abu Dhabi, United Arab Emirates' },
  { value: '103644278', label: 'United States' },
  { value: '90000070', label: 'New York City, United States' },
  { value: '102380872', label: 'Boston, United States' },
  { value: '102394087', label: 'Miami, United States' },
  { value: '102277331', label: 'San Francisco, United States' },
  { value: '103743442', label: 'Houston, United States' },
  { value: '102845717', label: 'Albania' },
  { value: '105333783', label: 'Bulgaria' },
  { value: '100456013', label: 'Finland' },
  { value: '104677530', label: 'Greece' },
  { value: '105238872', label: 'Iceland' },
  { value: '103819153', label: 'Norway' },
  { value: '103119917', label: 'Slovakia' },
  { value: '106137034', label: 'Slovenia' },
  { value: '106670623', label: 'Romania' },
  { value: '105076658', label: 'Warsaw, Mazowieckie, Poland' },
  { value: '100288700', label: 'Hungary' },
  { value: '106079947', label: 'Budapest, Hungary' },
  { value: '107144641', label: 'Vienna, Austria' },
  { value: '106156085', label: 'Prague, Czechia' },
  { value: '104508036', label: 'Czechia' },
  { value: '104406358', label: 'Geneva, Switzerland' },
  { value: '100467493', label: 'Rotterdam, South Holland, Netherlands' },
  { value: '105178154', label: 'Dublin, County Dublin, Ireland' },
  { value: '103173899', label: 'Kyiv City, Ukraine' },
  { value: '105080838', label: 'New York, United States' },
  { value: '102380872', label: 'Boston, Massachusetts, United States' },
  { value: '90000084', label: 'San Francisco Bay Area' },
  { value: '102095887', label: 'California, United States' },
];

export const linkedinNumberOfEmployees: LinkedinFilterValue[] = [
  { id: 'B', label: '1-10' },
  { id: 'C', label: '11-50' },
  { id: 'D', label: '51-200' },
  { id: 'E', label: '201-500' },
  { id: 'F', label: '501-1000' },
  { id: 'G', label: '1001-5000' },
  { id: 'H', label: '5001-10000' },
  { id: 'I', label: '10001+' },
];

export const rowPlaceholderFillOpacities = [
  0.49, 0.44, 0.39, 0.34, 0.29, 0.24, 0.19, 0.14,
];

export const planFeatures: {
  FreeTrial: string[];
  Starter: string[];
  Pro: string[];
  Ultra: string[];
  Custom: string[];
} = {
  [SubscriptionPlanType.FREE_TRIAL]: [
    'Email Finder (50 credits)',
    'Mobile Number Finder (5 credits)',
    'AI writing credits (500 credits)',
    'Unlimited email validation',
    'Browser Extension (on LinkedIn)',
    'Basic CRM integration',
    'Multichannel sequences',
    'Mailbox integration',
    'Aircall Integration',
    'AI Sequences and templates',
    'Full task automations',
    'Email and LinkedIn Tracking',
    'Automatic Sequence / Template Translator',
    'Deals',
    'Task Manager',
    'Full Analytics & Reports',
    'Signals (Job Changes, Companies news, etc)',
    'Limit of 100 contacts/companies',
  ],
  [SubscriptionPlanType.STARTER]: [
    'Email Finder (300 credits)',
    'Mobile Number Finder (30 credits)',
    'AI writing credits (3000 credits)',
    'Unlimited email validation',
    'Browser Extension (on LinkedIn)',
    'Basic CRM Integration',
    'CSV file import ',
    'Export lists of contacts (unlimited)',
    'LinkedIn sequences',
    'AI Relevance at scale (rewrite)',
    'Real-time contacts insights',
    'LinkedIn Tracking',
    'LinkedIn analytics & reports',
    'Sync data from LinkedIn to CRM',
    'Suggestions / Live feed',
    'Chat Support / Help Center',
  ],
  [SubscriptionPlanType.PRO]: [
    'Multichannel sequences (unlimited)',
    '3 Mailboxes per user',
    'Email Finder (500 credits)',
    'Mobile Number Finder (50 credits)',
    'AI writing credits (5.000 credits)',
    'Real-time CRM Integration (HubSpot, Pipedrive)',
    'Aircall Integration',
    'Import/export of CSV file',
    'Full Analytics & Reports',
    'Real-time contacts insights',
    'Chat Support / Help Center',
  ],
  [SubscriptionPlanType.ULTRA]: [
    'Everything in Pro, plus',
    '10 mailboxes per user',
    'Email Finder (1.000 credits)',
    'Mobile Number Finder (100 credits)',
    'AI writing credits (10.000 credits)',
    `Real-time CRM Integration (${getLabelForIntegrationType(IntegrationType.SALESFORCE)})`,
    'Integrated Voip Dialer',
    'API (Contacts, Companies, Activities, Deals)',
    'Signals (Job Changes, Companies News, Web Visits etc)',
    'Free Manager Seat (monitor only)',
    'Personalised 1:1 onboarding',
    'Dedicated Customer Success Manager',
  ],
  [SubscriptionPlanType.Custom]: [
    'Everything in Ultra, plus',
    'Tailored credit plans',
    'Unlimited mailboxes',
    'Access to all integrations',
    'Custom CRM Fields Mapping',
    'Sales Processes Consulting',
    'Templates and Strategies Consulting',
    'Weekly team performance report',
    'Personalized coaching to boost SDR & AE performance included',
  ],
};

export const stagesColors = {
  pink: { light: '#FFE3F0', text: '#FD67AF', dark: '#FD67AF' },
  fucsia: { light: '#FDE5F8', text: '#E769CB', dark: '#F376D8' },
  purple: { light: '#F2EAFF', text: '#8846DC', dark: '#CE69E7' },
  deepblue: { light: '#E4E4FF', text: '#6D73FF', dark: '#6D73FF' },
  blue: { light: '#DEECFF', text: '#3B85E8', dark: '#4E9AFF' },
  lightBlue: { light: '#E1F2FF', text: '#20A0DF', dark: '#2DC2F1' },
  acquamarine: { light: '#DBF2F2', text: '#0A9B94', dark: '#1EBAB2' },
  lime: { light: '#EDF7C8', text: '#71B161', dark: '#71B161' },
  yellow: { light: '#FEF3D2', text: '#C69812', dark: '#F9BB06' },
  orange: { light: '#FFEBDD', text: '#E38D3D', dark: '#FF8D5C' },
  green: { light: '#D3EEDD', text: '#1A9D6E', dark: '#24BA84' },
  red: { light: '#FFE2E2', text: '#ED4C5E', dark: '#FD6778' },
};

export interface StageColors {
  light: string;
  text: string;
  dark: string;
}

export const wonAndLostColors: StageColors[] = [
  stagesColors.green,
  stagesColors.red,
];

export const stagesColorsMap: { [key: number]: StageColors[] } = {
  2: [
    stagesColors.blue,
    stagesColors.yellow,
    // stagesColors.green,
    // stagesColors.red,
  ],
  3: [
    stagesColors.fucsia,
    stagesColors.blue,
    stagesColors.yellow,
    // stagesColors.green,
    // stagesColors.red,
  ],
  4: [
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.blue,
    stagesColors.yellow,
    // stagesColors.green,
    // stagesColors.red,
  ],
  5: [
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.blue,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
  6: [
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.blue,
    stagesColors.lightBlue,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
  7: [
    stagesColors.pink,
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.blue,
    stagesColors.lightBlue,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
  8: [
    stagesColors.pink,
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.deepblue,
    stagesColors.blue,
    stagesColors.lightBlue,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
  9: [
    stagesColors.pink,
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.deepblue,
    stagesColors.blue,
    stagesColors.lightBlue,
    stagesColors.acquamarine,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
  10: [
    stagesColors.pink,
    stagesColors.fucsia,
    stagesColors.purple,
    stagesColors.deepblue,
    stagesColors.blue,
    stagesColors.lightBlue,
    stagesColors.acquamarine,
    stagesColors.lime,
    stagesColors.yellow,
    stagesColors.orange,
    // stagesColors.green,
    // stagesColors.red,
  ],
};

export class BEConstants {
  static BadRequestStatusCode = 400;
}
